import { PerspectiveCamera } from '@react-three/drei';
import { ObjectMap, useFrame } from '@react-three/fiber';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Object3D, Vector3 } from 'three';
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';
import { BVH } from 'three/examples/jsm/loaders/BVHLoader.js';
import { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';

import { sceneDeclaresYUp } from '@cartwhl/3d/gltf/utils';
import ContactShadows from '@cartwhl/3d/r3f/components/ContactShadow';
import Lights from '@cartwhl/3d/r3f/components/Lights';
import { View } from '@cartwhl/3d/r3f/components/View';
import useFrameMixer from '@cartwhl/3d/r3f/hooks/useFrameMixer';
import { useSyncedFrameMixers } from '@cartwhl/3d/r3f/hooks/useSyncedFrameMixers';
import Text from '@cartwhl/ui/base/Text';
import PauseIcon from '@cartwhl/ui/icons/PauseIcon';
import PlayIcon from '@cartwhl/ui/icons/PlayIcon';

const cameraPosition = new Vector3(0, 0.8, 5);

export default function ScissorView({
  className,
  animationData,
  meshData,
  frameRate,
  playbackSpeed,
  frameStepSize,
  mixerSync,
  showDetails,
}: {
  className: string;
  animationData: BVH;
  meshData: GLTF & ObjectMap;
  frameRate: number;
  playbackSpeed: number;
  frameStepSize: number;
  mixerSync?: ReturnType<typeof useSyncedFrameMixers>;
  showDetails?: boolean;
}) {
  const [playStatus, setPlayStatus] = useState(false);
  const [frame, setFrame] = useState(0);

  const clonedScene = useMemo(
    () => SkeletonUtils.clone(meshData?.scene),
    [meshData],
  );

  // Force hide the axel clothes after it shows up.
  // it's supposed to get cleaned up already but often the clonedScene happens before the meshData is finished being cleaned
  clonedScene.traverse((child) => {
    if (child.name.endsWith('_RND')) {
      child.visible = false;
    }
  });

  const mixer = useFrameMixer(animationData.clip, clonedScene, {
    frameRate,
    playbackSpeed,
    frameStepSize,
    syncController: mixerSync,
    onFrameUpdate: setFrame,
    onPlayChange: setPlayStatus,
  });

  return (
    <>
      {showDetails && mixer && (
        <div className="text-gray-60 absolute right-2 bottom-1 left-2 z-10 flex justify-between gap-2">
          <div>
            {playStatus ? (
              <PlayIcon className="mt-0.5 h-2.5 w-2.5" />
            ) : (
              <PauseIcon className="mt-0.5 h-2.5 w-2.5" />
            )}
          </div>
          <Text variant="studio/body/sm">
            {Math.round(mixer.duration.seconds * 100) / 100}s
          </Text>
        </div>
      )}
      <div
        className={`absolute bottom-0 left-0 h-[2px] transition-colors duration-300 ${
          frame === 1 ? 'bg-[#dadada]' : 'bg-[#cdcdcd]'
        }`}
        style={
          !mixer
            ? {}
            : frame === 1
              ? { width: '100%' }
              : {
                  width: `${(frame / mixer.duration.frames) * 100}%`,
                }
        }
      />
      <View className={className}>
        <ScissorScene scene={clonedScene} mixer={mixer} />
      </View>
    </>
  );
}

function ScissorScene({
  scene,
  mixer,
}: {
  scene: Object3D;
  mixer: ReturnType<typeof useFrameMixer>;
}) {
  useFrame((state, delta) => {
    mixer.update(delta);
  });

  const didSyncRef = useRef(false);
  useEffect(() => {
    if (didSyncRef.current) return;
    didSyncRef.current = true;
    // Firing off an event like this forces a canvas refresh which
    // fixed a bug where the variation character had their head cutting through the top of the box
    requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
  }, []);

  return (
    <>
      <Lights />
      <ContactShadows />
      <PerspectiveCamera makeDefault position={cameraPosition} zoom={2} />
      <primitive
        object={scene}
        rotation={sceneDeclaresYUp(scene) ? undefined : [-Math.PI / 2, 0, 0]}
      />
    </>
  );
}
